import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import config from "../../../config";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { LiveFeed, RELAY_CHANNELS } from "../../relay-live";
import { LiveTranscriptionsPanel } from "../../logger/components/LiveTranscriptionsPanel";
import {
  ParticipantsPanel,
  type ParticipantDisplayMode,
} from "../../logger/components/ParticipantsPanel";

const RELAY_ACCENT = "#22D3EE";

// IP pública documentada del droplet de DigitalOcean que re-sirve el egress WHEP
// en vivo (ver relay/CLAUDE.md → "Droplet relay-whep-public"). Es solo
// informativa para el panel; el endpoint real que consume el <video> es
// config.RELAY_WHEP_BASE.
const DIGITALOCEAN_EGRESS = {
  provider: "DigitalOcean",
  droplet: "relay-whep-public",
  ip: "159.89.183.167",
  region: "nyc3",
  service: "MediaMTX v1.19 · WHEP egress :8889",
};

// Asignaciones de speaker → participante hechas por drag&drop en el feed.
// Igual que en el logger (ConsoleColumn), es estado LOCAL de monitoreo: no se
// persiste ni se emite al server, solo re-etiqueta las transcripciones visibles.
interface SpeakerAssignment {
  speakerId: string;
  name: string;
  assignedAt: number;
}

interface RelayMonitorPanelProps {
  participants?: any[];
  // Logs en vivo ya aplanados/ordenados (newest-first) como en la vista Live Feed.
  logs?: any[];
  feedLoading?: boolean;
  onRefreshLogs?: () => void;
  getCameraName?: (id: string) => string;
  getTagColor?: (id: string) => string;
  getTagLabel?: (id: string) => string;
}

// Tarjeta de un par etiqueta/valor del relay (endpoints, ids, etc.).
const InfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      padding: "8px 0",
      borderBottom: "1px solid rgba(255,255,255,0.04)",
    }}
  >
    <span
      style={{
        fontSize: "0.7rem",
        color: "var(--color-text-muted)",
        fontWeight: 700,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: "0.7rem",
        color: "#ddd",
        fontFamily: mono ? "var(--font-mono)" : undefined,
        textAlign: "right",
        wordBreak: "break-all",
      }}
    >
      {value}
    </span>
  </div>
);

// Un player compacto para UNA cámara (video-1 o video-2), con toggle de pista de
// audio A/B. Cada pista de audio es un path WHEP distinto en MediaMTX, así que
// cambiar de audio remonta el <LiveFeed> (key=channelId) con el nuevo path.
interface AudioOption {
  id: string;
  label: string;
}
const RelayVideoTile: React.FC<{
  title: string;
  audioChannels: AudioOption[];
}> = ({ title, audioChannels }) => {
  const [channelId, setChannelId] = useState(audioChannels[0]?.id);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        background: "var(--color-panel-bg)",
        border: "1px solid var(--color-panel-border)",
        borderRadius: "12px",
        padding: "12px",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <i className="bx bx-video" style={{ fontSize: "0.95rem", color: RELAY_ACCENT }}></i>
        <span
          style={{
            fontSize: "0.7rem",
            fontWeight: 800,
            letterSpacing: "1px",
            color: RELAY_ACCENT,
            textTransform: "uppercase",
          }}
        >
          {title}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
          {audioChannels.map((a) => (
            <button
              key={a.id}
              onClick={() => setChannelId(a.id)}
              title={a.id}
              style={{
                background: channelId === a.id ? RELAY_ACCENT : "transparent",
                color: channelId === a.id ? "#0b0b0b" : "var(--color-text-muted)",
                border: `1px solid ${channelId === a.id ? RELAY_ACCENT : "var(--color-panel-border)"}`,
                borderRadius: "5px",
                padding: "2px 8px",
                fontSize: "0.58rem",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
      {/* key=channelId: al cambiar de pista de audio remonta y reconecta el WHEP */}
      <LiveFeed key={channelId} initialChannelId={channelId} showChannelSelector={false} />
    </div>
  );
};

const RelayInfoCard: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        background: "var(--color-panel-bg)",
        border: "1px solid var(--color-panel-border)",
        borderRadius: "12px",
        padding: "16px 18px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <i className="bx bx-server" style={{ fontSize: "1rem", color: RELAY_ACCENT }}></i>
        <span
          style={{
            fontSize: "0.75rem",
            fontWeight: 800,
            letterSpacing: "1px",
            color: RELAY_ACCENT,
            textTransform: "uppercase",
          }}
        >
          {t("admin.relay.infoTitle", "Información del relay")}
        </span>
      </div>

      {/* Egress en vivo (DigitalOcean) */}
      <div
        style={{
          background: "rgba(34, 211, 238, 0.06)",
          border: `1px solid ${RELAY_ACCENT}33`,
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            fontSize: "0.6rem",
            fontWeight: 800,
            letterSpacing: "0.5px",
            color: RELAY_ACCENT,
            marginBottom: "6px",
            textTransform: "uppercase",
          }}
        >
          {t("admin.relay.liveEgress", "Egress en vivo")} · {DIGITALOCEAN_EGRESS.provider}
        </div>
        <InfoRow label="Droplet" value={DIGITALOCEAN_EGRESS.droplet} />
        <InfoRow label="IP" value={DIGITALOCEAN_EGRESS.ip} mono />
        <InfoRow label={t("admin.relay.region", "Región")} value={DIGITALOCEAN_EGRESS.region} />
        <InfoRow label={t("admin.relay.service", "Servicio")} value={DIGITALOCEAN_EGRESS.service} />
      </div>

      <InfoRow label="WHEP base" value={config.RELAY_WHEP_BASE} mono />
      <InfoRow label="Worker base" value={config.RELAY_WORKER_BASE} mono />
      <InfoRow label="Project ID" value={config.RELAY_PROJECT_ID} mono />
      <InfoRow
        label={t("admin.relay.defaultChannel", "Canal por defecto")}
        value={config.RELAY_DEFAULT_CHANNEL}
      />

      <div style={{ marginTop: "12px" }}>
        <div
          style={{
            fontSize: "0.6rem",
            fontWeight: 800,
            letterSpacing: "0.5px",
            color: "var(--color-text-muted)",
            marginBottom: "8px",
            textTransform: "uppercase",
          }}
        >
          {t("admin.relay.channels", "Canales")} ({RELAY_CHANNELS.length})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {RELAY_CHANNELS.map((c) => (
            <span
              key={c.id}
              title={c.id}
              style={{
                fontSize: "0.62rem",
                fontWeight: 700,
                padding: "4px 10px",
                borderRadius: "6px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid var(--color-panel-border)",
                color: "#bbb",
              }}
            >
              {c.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Feed de logs en vivo (solo lectura): misma fuente que la vista "Live Feed"
// del superadmin (`globalLogs`), en formato compacto para el monitor de relay.
const RelayLogsFeed: React.FC<{
  logs: any[];
  loading?: boolean;
  participants: any[];
  onRefresh?: () => void;
  getCameraName?: (id: string) => string;
  getTagColor?: (id: string) => string;
  getTagLabel?: (id: string) => string;
}> = ({ logs, loading, participants, onRefresh, getCameraName, getTagColor, getTagLabel }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // El feed llega newest-first; para autoscroll mostramos cronológico (oldest→newest).
  const ordered = React.useMemo(() => [...logs].reverse(), [logs]);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [ordered.length, autoScroll]);

  const handleScroll = () => {
    const c = containerRef.current;
    if (!c) return;
    setAutoScroll(Math.abs(c.scrollHeight - c.scrollTop - c.clientHeight) < 50);
  };

  return (
    <div
      data-testid="relay-logs-feed"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "var(--color-panel-bg)",
        border: "1px solid var(--color-panel-border)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "12px 16px",
          borderBottom: "1px solid var(--color-panel-border)",
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: RELAY_ACCENT,
            boxShadow: `0 0 8px ${RELAY_ACCENT}`,
          }}
        />
        <span
          style={{
            fontSize: "0.75rem",
            fontWeight: 800,
            letterSpacing: "1px",
            color: RELAY_ACCENT,
            textTransform: "uppercase",
          }}
        >
          {t("admin.relay.logsTitle", "Feed de logs")}
        </span>
        <span
          style={{
            fontSize: "0.6rem",
            color: RELAY_ACCENT,
            background: "rgba(34, 211, 238, 0.1)",
            padding: "2px 8px",
            borderRadius: "10px",
            fontWeight: 700,
          }}
        >
          {logs.length}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
          <button
            onClick={() => setAutoScroll((v) => !v)}
            style={{
              background: autoScroll ? "rgba(34, 211, 238, 0.1)" : "transparent",
              border: `1px solid ${autoScroll ? RELAY_ACCENT : "var(--color-panel-border)"}`,
              color: autoScroll ? RELAY_ACCENT : "var(--color-text-muted)",
              borderRadius: "6px",
              padding: "3px 10px",
              fontSize: "0.6rem",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            {autoScroll ? "⏬ AUTO" : "⏸"}
          </button>
          {onRefresh && (
            <button
              onClick={onRefresh}
              style={{
                background: "transparent",
                border: "1px solid var(--color-panel-border)",
                color: "var(--color-text-muted)",
                borderRadius: "6px",
                padding: "3px 10px",
                fontSize: "0.6rem",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              ↻
            </button>
          )}
        </div>
      </div>

      <div ref={containerRef} onScroll={handleScroll} style={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <div className="loader"></div>
          </div>
        ) : ordered.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              opacity: 0.45,
              gap: "8px",
            }}
          >
            <i className="bx bx-broadcast" style={{ fontSize: "2rem" }}></i>
            <span style={{ fontSize: "0.85rem" }}>
              {t("admin.relay.noLogs", "Sin logs todavía")}
            </span>
          </div>
        ) : (
          ordered.map((log: any) => (
            <div
              key={log.id}
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid var(--color-panel-border)",
                fontSize: "0.8rem",
                borderLeft: (log.rating || 0) > 0 ? "3px solid #ffaa00" : "3px solid transparent",
              }}
            >
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ minWidth: "58px" }}>
                  <div
                    style={{
                      color: "rgba(255,255,255,0.8)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.72rem",
                    }}
                  >
                    {log.tc}
                  </div>
                  <div
                    style={{
                      fontSize: "0.58rem",
                      color: "var(--color-accent-success)",
                      fontWeight: 900,
                      marginTop: "2px",
                    }}
                  >
                    {log.author}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      color: "var(--color-text-secondary)",
                      lineHeight: 1.4,
                    }}
                  >
                    {log.text &&
                      log.text.split(/(\[.*?\])/g).map((part: string, i: number) =>
                        part.startsWith("[") && part.endsWith("]") ? (
                          <span
                            key={i}
                            style={{
                              color: "rgba(255,255,255,0.9)",
                              fontWeight: 900,
                              margin: "0 2px",
                              textTransform: "uppercase",
                            }}
                          >
                            {part.slice(1, -1)}
                          </span>
                        ) : (
                          part
                        ),
                      )}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      marginTop: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    {log.cameras?.map((c: string) => {
                      const camName = getCameraName?.(c);
                      return camName ? (
                        <span key={c} style={badgeStyle("#ffaa00", "#000")}>
                          {camName}
                        </span>
                      ) : null;
                    })}
                    {log.participants?.map((pId: string) => {
                      const p = participants.find(
                        (pp: any) => pp.id?.toLowerCase() === pId.toLowerCase(),
                      );
                      if (!p) return null;
                      return (
                        <span
                          key={pId}
                          style={badgeStyle("var(--color-accent-danger)", "rgba(255,255,255,0.9)")}
                        >
                          {p.number ? `${p.number} ${p.alias || p.name}` : p.alias || p.name}
                        </span>
                      );
                    })}
                    {log.tags?.map((tg: string) => (
                      <span key={tg} style={badgeStyle(getTagColor?.(tg) || "#888", "#000")}>
                        {getTagLabel?.(tg) || tg}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Estilo común de los badges (cámara/participante/tag) del feed de logs.
const badgeStyle = (bg: string, color: string): React.CSSProperties => ({
  fontSize: "0.56rem",
  background: bg,
  color,
  padding: "0 6px",
  height: "15px",
  borderRadius: "20px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.2px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
});

// Panel de participantes del monitor de relay. Reusa el mismo ParticipantsPanel
// del logger. El gesto soportado es arrastrar un participante sobre una
// transcripción del feed (o soltar una transcripción aquí) para etiquetar al
// speaker — sin creación de logs, es una vista de monitoreo. El modo/tamaño se
// guardan en claves propias para no pisar la preferencia del logger.
const RelayParticipantsCard: React.FC<{
  participants: any[];
  onDropItem: (p: any, speakerId: string, sourceTimestamp?: number) => void;
}> = ({ participants, onDropItem }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useLocalStorage<ParticipantDisplayMode>("relay_participantMode", "photo");
  const [size, setSize] = useLocalStorage<number>("relay_participantSize", 88);
  return (
    <div
      data-testid="relay-participants"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "var(--color-panel-bg)",
        border: "1px solid var(--color-panel-border)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", minHeight: 0 }}>
        <ParticipantsPanel
          participants={participants}
          title={t("admin.relay.participantsTitle", "Participantes")}
          mode={mode}
          onModeChange={setMode}
          size={size}
          onSizeChange={setSize}
          onDropItem={onDropItem}
          selectedColor={RELAY_ACCENT}
          modeLabels={{
            photo: t("logger.console.photo", "FOTO"),
            number: t("logger.console.text", "TEXTO"),
            code: t("logger.console.code", "CÓDIGO"),
          }}
          modeTooltip={t("logger.console.cycleDisplay", "Cambiar vista")}
        />
      </div>
    </div>
  );
};

/**
 * Sección RELAY del superadmin: combina el video en vivo del egress WHEP
 * (servido desde el droplet de DigitalOcean), el feed de transcripciones en
 * vivo (mismo LiveTranscriptionsPanel del logger, con drag&drop de
 * participantes para etiquetar speakers), el feed de logs en tiempo real, el
 * panel de participantes y la información de configuración del relay.
 */
export const RelayMonitorPanel: React.FC<RelayMonitorPanelProps> = ({
  participants = [],
  logs = [],
  feedLoading = false,
  onRefreshLogs,
  getCameraName,
  getTagColor,
  getTagLabel,
}) => {
  // Estado LOCAL de etiquetado speaker→participante (drag&drop). No se persiste
  // ni se emite — igual que en el logger (ConsoleColumn).
  const [speakerAssignments, setSpeakerAssignments] = useState<SpeakerAssignment[]>([]);

  const addSpeakerAssignment = (speakerId: string, name: string, timestamp?: number) => {
    if (!speakerId || !name) return;
    setSpeakerAssignments((prev) => [
      ...prev,
      {
        speakerId,
        name: name.toUpperCase(),
        assignedAt: timestamp !== undefined ? Number(timestamp) : Date.now(),
      },
    ]);
  };

  return (
    <div
      data-testid="admin-view-relay"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 2fr) minmax(320px, 1fr)",
        gridTemplateRows: "auto minmax(0, 1fr)",
        gap: "16px",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Video en vivo (WHEP / DigitalOcean): dos players — Video 1 y Video 2 */}
      <div
        style={{
          gridColumn: "1",
          gridRow: "1",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          minWidth: 0,
        }}
      >
        <RelayVideoTile
          title="Video 1"
          audioChannels={[
            { id: "feed_audio_a", label: "Audio A" },
            { id: "feed_audio_b", label: "Audio B" },
          ]}
        />
        <RelayVideoTile
          title="Video 2"
          audioChannels={[
            { id: "feed_audio_c", label: "Audio A" },
            { id: "feed_audio_d", label: "Audio B" },
          ]}
        />
      </div>

      {/* Info del relay (columna derecha, fila superior) */}
      <div style={{ gridColumn: "2", gridRow: "1" }}>
        <RelayInfoCard />
      </div>

      {/* Fila inferior (ancho completo): 3 columnas —
          transcripciones · participantes · logs. Arrastrar un participante
          sobre una transcripción del feed etiqueta al speaker. */}
      <div
        style={{
          gridColumn: "1 / -1",
          gridRow: "2",
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)",
          gap: "16px",
        }}
      >
        <div style={{ minHeight: 0, minWidth: 0 }}>
          <LiveTranscriptionsPanel
            baseTc=""
            participants={participants}
            isCollapsed={false}
            speakerAssignments={speakerAssignments}
            onSpeakerMap={(source, target, timestamp) =>
              addSpeakerAssignment(source, target, timestamp)
            }
            // Vista de monitoreo: no se crean logs, solo se etiquetan speakers.
            onSelectText={() => {}}
          />
        </div>

        <div style={{ minHeight: 0, minWidth: 0 }}>
          <RelayParticipantsCard
            participants={participants}
            onDropItem={(p, speakerId, sourceTimestamp) =>
              addSpeakerAssignment(
                speakerId,
                (p.alias || p.name || "").toUpperCase(),
                sourceTimestamp,
              )
            }
          />
        </div>

        <div style={{ minHeight: 0, minWidth: 0 }}>
          <RelayLogsFeed
            logs={logs}
            loading={feedLoading}
            participants={participants}
            onRefresh={onRefreshLogs}
            getCameraName={getCameraName}
            getTagColor={getTagColor}
            getTagLabel={getTagLabel}
          />
        </div>
      </div>
    </div>
  );
};

export default RelayMonitorPanel;
